import React, { useState, useEffect } from 'react';
import { Target, Layers, ChevronRight, Sun, Moon } from 'lucide-react';

export default function LandingPage({ onLoginClick, t, darkMode, setDarkMode }) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const eyebrow = { fontFamily: t.mono, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: t.textMuted };

    return (
        <div style={{
            minHeight: '100vh',
            color: t.text,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: isMobile ? '28px 16px 40px' : '40px 20px 56px',
            position: 'relative',
        }}>
            {/* Theme toggle */}
            <button
                onClick={() => setDarkMode(!darkMode)}
                style={{
                    position: 'absolute', top: 22, right: 22, zIndex: 10,
                    width: 42, height: 42, borderRadius: 0,
                    background: t.bgCard, border: `1.5px solid ${t.ink}`,
                    color: t.text, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                }}
            >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Wordmark / hero */}
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 32 : 48, width: '100%', maxWidth: 900 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 18 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.meli }} />
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.shopee }} />
                    <span style={{ ...eyebrow, marginLeft: 4 }}>Precificação para marketplaces</span>
                </div>
                <h1 style={{
                    fontFamily: t.disp, fontSize: isMobile ? 56 : 76, fontWeight: 700, margin: 0,
                    letterSpacing: '-0.04em', lineHeight: 0.95, color: t.text,
                }}>
                    Prexo<span style={{ color: t.shopee }}>.</span>
                </h1>
                <p style={{ ...eyebrow, marginTop: 12, fontSize: 11, letterSpacing: '0.2em' }}>
                    Onde o preço nasce
                </p>
            </div>

            {/* Extrato panel */}
            <div style={{
                maxWidth: 920, width: '100%',
                background: t.bgCard,
                border: `1.5px solid ${t.ink}`,
                padding: isMobile ? '28px 22px' : '44px 48px',
                position: 'relative',
            }}>
                {/* faixas das marketplaces no topo */}
                <div style={{ position: 'absolute', top: -1.5, left: -1.5, right: -1.5, height: 4, display: 'flex' }}>
                    <span style={{ flex: 1, background: t.meli }} />
                    <span style={{ flex: 1, background: t.shopee }} />
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
                    gap: isMobile ? 28 : 52,
                    alignItems: 'center',
                }}>
                    <div>
                        <span style={eyebrow}>A ciência do preço</span>
                        <h2 style={{ fontFamily: t.disp, fontSize: isMobile ? 24 : 30, fontWeight: 700, lineHeight: 1.2, margin: '10px 0 18px', color: t.text, letterSpacing: '-0.01em' }}>
                            Saiba quanto sobra <span style={{ color: t.profit }}>antes</span> de anunciar.
                        </h2>
                        <p style={{ fontSize: isMobile ? 14 : 15, color: t.textSecondary, lineHeight: 1.65, marginBottom: 14 }}>
                            Cada marketplace tem suas regras de comissão, frete, taxas e impostos.
                            O Prexo disseca cada uma e te mostra <strong style={{ color: t.text }}>o preço de venda exato</strong> pra bater a sua margem.
                        </p>
                        <p style={{ fontSize: isMobile ? 13 : 14, color: t.textMuted, lineHeight: 1.6, margin: 0, fontFamily: t.mono }}>
                            Mercado Livre · Shopee · tabela oficial de frete.
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {[
                            { Icon: Target, color: t.meli, title: 'Decisão com método', desc: 'O preço deixa de ser no "feeling".' },
                            { Icon: Layers, color: t.shopee, title: 'Margem protegida', desc: 'Simule cenários antes do mercado.' },
                        ].map(({ Icon, color, title, desc }, i) => (
                            <div key={i} style={{
                                background: t.bg, border: `1.5px solid ${t.ink}`, padding: isMobile ? 16 : 18,
                                display: 'flex', gap: 13, alignItems: 'center', position: 'relative', overflow: 'hidden',
                            }}>
                                <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: color }} />
                                <Icon size={20} style={{ color: t.ink, flexShrink: 0, marginLeft: 4 }} />
                                <div>
                                    <h4 style={{ margin: '0 0 2px 0', fontFamily: t.disp, fontSize: isMobile ? 14 : 15, fontWeight: 700, color: t.text }}>{title}</h4>
                                    <p style={{ margin: 0, fontSize: isMobile ? 12 : 12.5, color: t.textMuted }}>{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <div style={{
                    marginTop: isMobile ? 28 : 40, paddingTop: isMobile ? 24 : 32,
                    borderTop: `1.5px dashed ${t.lineDot}`,
                    display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 20,
                }}>
                    <p style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, margin: 0, maxWidth: 460, textAlign: isMobile ? 'center' : 'left' }}>
                        Compare canais, simule e <span style={{ color: t.text, fontWeight: 600 }}>proteja sua margem antes de vender</span>.
                    </p>
                    <button
                        onClick={onLoginClick}
                        onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { if (!isMobile) e.currentTarget.style.transform = 'translateY(0)'; }}
                        style={{
                            background: t.ink, color: t.bg, border: 'none',
                            padding: isMobile ? '14px 24px' : '15px 30px', borderRadius: 0,
                            fontFamily: t.mono, fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            transition: 'transform 0.15s',
                        }}
                    >
                        Entrar no Prexo <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            {/* Footer / barcode */}
            <div style={{ marginTop: 36, textAlign: 'center' }}>
                <div className="px-barcode" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontFamily: t.mono, fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: t.textMuted }}>
                    Prexo v5.9.4 · © 2026 inteligência em precificação
                </div>
            </div>
        </div>
    );
}
